import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';
import { useDismissible } from '@/hooks/learner/useMemberConfig';
import { palette } from '@/styles/figma-tokens';

interface ReminderBannerProps {
  storageKey: string;
  title: string;
  message: string;
  onDismiss?: () => void;
}

/**
 * ReminderBanner — matches Figma node 6810:82433.
 *
 * Orange-tinted card with close (×) button top-right, centered title + body,
 * and a "Never show it again" dismiss button at the bottom. Dismissed state
 * persists to the member's `/me/config` (carries across devices); `storageKey`
 * is the config key. Render is gated on loaded config so it never flashes.
 */
export default function ReminderBanner({ storageKey, title, message, onDismiss }: ReminderBannerProps) {
  const { dismissed, isLoading, dismiss } = useDismissible(storageKey);

  const handleDismiss = () => {
    dismiss();
    onDismiss?.();
  };

  return (
    <AnimatePresence>
      {!isLoading && !dismissed && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.25, ease: EASE_OUT }}
          style={{ overflow: 'hidden' }}
        >
          <div
            style={{
              background: 'rgb(var(--color-secondary) / 0.08)',
              border: '1px solid rgb(var(--color-secondary) / 0.4)',
              borderRadius: '16px',
              padding: '0 17px 24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '22px',
              marginBottom: '24px',
            }}
          >
            {/* Close (×) button — top right */}
            <div style={{ width: '100%', height: '36px', position: 'relative' }}>
              <button
                type="button"
                onClick={handleDismiss}
                aria-label="Dismiss"
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '37px',
                  height: '36px',
                  borderRadius: '30px',
                  border: `1px solid ${'rgb(var(--color-secondary))'}`,
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  padding: 0,
                  marginTop: '8px',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgb(var(--color-secondary) / 0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    style={{ stroke: 'rgb(var(--color-secondary))' }}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* Title + Body */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '20px',
                maxWidth: '310px',
                textAlign: 'center',
              }}
            >
              <p
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  fontSize: '24px',
                  lineHeight: '36px',
                  color: 'rgb(var(--color-text-primary))',
                  margin: 0,
                }}
              >
                {title}
              </p>
              <p
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: 'rgb(var(--color-text-primary))',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {message}
              </p>
            </div>

            {/* "Never show it again" button */}
            <button
              type="button"
              onClick={handleDismiss}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                minHeight: '36px',
                minWidth: '64px',
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid rgb(var(--color-border-primary) / 0.6)',
                background: 'transparent',
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 700,
                fontSize: '14px',
                lineHeight: '24px',
                color: 'rgb(var(--color-text-primary))',
                cursor: 'pointer',
                transition: 'background 0.15s, border-color 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(189,188,201,0.12)';
                e.currentTarget.style.borderColor = palette.grey['600'];
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'rgb(var(--color-border-primary) / 0.6)';
              }}
            >
              {/* Sleepy eyes icon */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M3 12c0 0 3.5-6 9-6s9 6 9 6-3.5 6-9 6-9-6-9-6z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.6"
                />
                <path
                  d="M2 2l20 20"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              Never show it again
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
